import React from 'react'
import { withRouter } from 'react-router-dom'

import { WithOptimizelyProps } from '@optimizely/react-sdk'
import { compose, fromRenderProps, lifecycle, withHandlers, withProps } from 'recompose'

import OneTimeSVG from 'assets/images/one-time.svg'
import RecurringSVG from 'assets/images/recurring.svg'
import RoundUpsPNG from 'assets/images/round-ups.png'
import { CTAContainer } from 'components/CTAContainer'
import withRemoteStrings, { RemoteStringsProps } from 'components/HOCs/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { withOptimizely } from 'utils/Optimizely'

import {
  Container,
  ContentContainer,
  ContentText,
  Image,
  InvestmentSubtitle,
  InvestmentTitle,
  InvestmentTypeContainer,
  StyledButton,
  Text,
  TextContainer,
  Title,
} from './styled-components'

type InvestmentType = {
  image: any
  title: string
  subtitle: string
}

type Props = {
  handleLinkFundingSource: () => void
  headerTitle: string
  headerBody: string
  bodyCopy: string
  linkFundingSourceCopy: string
  skipLinkingCopy: string
  investmentTypes: InvestmentType[]
} & WithOptimizelyProps &
  RemoteStringsProps &
  FunnelContextProps

const DEFAULT_COPY = {
  header_title_text: 'Ready to Invest',
  header_body_text: `You're about to become an investor! Here are 3 easy ways to
get started.`,
  body_copy_text: 'Before you can start investing, link a funding source first.',
  link_funding_source_cta_text: 'Link Funding Source',
  skip_linking_cta_text: 'Do this later',
  recurring_investment_title_text: 'Recurring Investment',
  recurring_investment_subtitle_text: 'Set it and forget it',
  one_time_investment_title_text: 'One-time Investment',
  one_time_investment_subtitle_text: 'Invest on the go',
  round_ups_title_text: 'Round-Up Investment',
  round_ups_subtitle_text: 'Invest as you spend',
}

const LinkFundingSourceButton = withClickTrack<any, any>(
  TRACKER_IDS.SECOND_CHANCE_BANK_LINK.LINK_FUNDING_SOURCE_BUTTON,
)(StyledButton)

export const enhance = compose<Props, Props>(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isBackEnabled, isDynamicFunnelEnabled, routeToPage }) => ({
      isBackEnabled,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withOptimizely,
  withRouter,
  withFlowRouter,
  withRemoteStrings,
  withProps(({ getRemoteString }) => {
    return {
      headerTitle: DEFAULT_COPY.header_title_text,
      headerBody: getRemoteString(
        'second_chance_bank_link_header_body',
        DEFAULT_COPY.header_body_text,
      ),
      bodyCopy: getRemoteString('second_chance_bank_link_body_copy', DEFAULT_COPY.body_copy_text),
      linkFundingSourceCopy: getRemoteString(
        'second_chance_bank_link_link_funding_source_cta_text',
        DEFAULT_COPY.link_funding_source_cta_text,
      ),
      skipLinkingCopy: getRemoteString(
        'second_chance_bank_link_skip_linking_cta_text',
        DEFAULT_COPY.skip_linking_cta_text,
      ),
      investmentTypes: [
        {
          image: RecurringSVG,
          title: getRemoteString(
            'second_chance_bank_link_recurring_investment_title',
            DEFAULT_COPY.recurring_investment_title_text,
          ),
          subtitle: getRemoteString(
            'second_chance_bank_link_recurring_investment_subtitle',
            DEFAULT_COPY.recurring_investment_subtitle_text,
          ),
        },
        {
          image: OneTimeSVG,
          title: getRemoteString(
            'second_chance_bank_link_one_time_investment_title',
            DEFAULT_COPY.one_time_investment_title_text,
          ),
          subtitle: getRemoteString(
            'second_chance_bank_link_one_time_investment_subtitle',
            DEFAULT_COPY.one_time_investment_subtitle_text,
          ),
        },
        {
          image: RoundUpsPNG,
          title: getRemoteString(
            'second_chance_bank_link_round_ups_title',
            DEFAULT_COPY.round_ups_title_text,
          ),
          subtitle: getRemoteString(
            'second_chance_bank_link_round_ups_subtitle',
            DEFAULT_COPY.round_ups_subtitle_text,
          ),
        },
      ],
    }
  }),
  lifecycle({
    componentDidMount() {
      this.props.trackOptimizely('second_chance.screen_viewed')
    },
  }),
  withHandlers({
    handleLinkFundingSource: ({
      isDynamicFunnelEnabled,
      route,
      routeToPage,
      trackOptimizely,
    }) => () => {
      trackOptimizely('link_your_bank.cta_clicked')
      isDynamicFunnelEnabled
        ? routeToPage(PageName.SecondChanceBankLink, ActionType.Continue)
        : route({ from: FlowRoute.SecondChanceBankLink, action: 'continue' })
    },
  }),
)

export const SecondChanceBankLink: React.FC<Props> = ({
  handleLinkFundingSource,
  headerTitle,
  headerBody,
  bodyCopy,
  linkFundingSourceCopy,
  investmentTypes,
  isDynamicFunnelEnabled,
  isBackEnabled,
}) => (
  <>
    <Nav back={isDynamicFunnelEnabled && isBackEnabled(PageName.SecondChanceBankLink)} />
    <Layout>
      <Container>
        <Title>{headerTitle}</Title>
        <Text>{headerBody}</Text>
        <ContentContainer>
          {investmentTypes.map(type => (
            <InvestmentTypeContainer key={type.title}>
              <Image image={type.image} />
              <TextContainer>
                <InvestmentTitle>{type.title}</InvestmentTitle>
                <InvestmentSubtitle>{type.subtitle}</InvestmentSubtitle>
              </TextContainer>
            </InvestmentTypeContainer>
          ))}
          <ContentText>{bodyCopy}</ContentText>
        </ContentContainer>
        <Separator />
        <CTAContainer>
          <LinkFundingSourceButton
            data-testid={TestIDs.SECOND_CHANCE.LINK_BANK_BUTTON}
            onPress={handleLinkFundingSource}
            buttonType={'submit'}
          >
            {linkFundingSourceCopy}
          </LinkFundingSourceButton>
        </CTAContainer>
      </Container>
    </Layout>
  </>
)

export default enhance(SecondChanceBankLink)
